import axios, { AxiosResponse } from 'axios';
import { isString, isEmpty, isNumber } from 'lodash';
import { PaidAudience, Population } from 'modules/population/model';
import { DeviceOsType, Order, PopulationType } from 'constants/constants';
import { ThemeAudience } from 'modules/theme/interface';
import {
    BusinessInterestsRep,
    ICascaderItem,
    InetCelebrity,
    PaidPopulationItem,
} from 'modules/population/interface';
import {
    PopulationDetail,
    PopulationDetailOverview,
    PopulationDetailPushRecord,
    ReportQuery,
    TableSourceItem,
} from 'modules/detail/config';
import { message } from 'm-ui';
import qs from 'qs';
import store from 'redux/store';
import { mockTimeout } from 'utils/axios';
import client from '../utils/httpClient';
import {
    BrandItem,
    GetSimilarBrandsResponse,
    SearchBrandByNameResponse,
} from '../modules/Form/component/BrandBehavior/interface';
import { FrontDetail } from 'modules/Form';
import { addRadarEvent } from 'utils/businesslog';
import { TagCode } from '@/modules/policys/populationPrice/PopulationLabelAdd/config';

let index = 0;
/**
 * 人群包定向相关 api
 */
interface Keyword {
    id: string;
    name: string;
    count: number;
}

// 品牌详情获取
export interface BrandSearchParams {
    brandName: string;
    page: number;
    pageSize: number;
}

export interface BrandsQuery {
    firstLabelId?: number;
    secondLabelId?: number;
    page: number;
    pageSize: number;
}

interface GetBrandResponse {
    pageInfo: {
        currentPage: number;
        pageSize: number;
        totalCount: number;
    };
    data: BrandItem[];
}

export interface IGroupInfo {
    groupId?: number;
    groupName?: string;
    orientationCnt?: number;
    canEdit?: boolean;
}

interface ParamDataItem {
    value: string;
    label: string;
    group: number;
    description?: string;
}

export interface ParamData {
    [key: string]: ParamDataItem[];
}
export interface BaseReq {
    typeName: string;
    // type: number;
    groupName?: string;
    orientationName: string;
    paramData: ParamData;
}

export interface CreatePopReq extends BaseReq {
    frontDetail: FrontDetail;
}

export interface ITagItem {
    itemCode?: string;
    itemValue?: string;
    itemChildren?: null;
}
export interface ILabelInfo {
    tagCode?: TagCode;
    tagName?: string;
    tagItems?: ITagItem[];
}

export interface IQuoteDetails {
    ruleType?: string;
    ruleContent?: (string | number)[];
    labelNameList?: string[];
    quote?: number;
}

export interface ICreatInfo {
    strategyName?: string;
    ruleScope?: number;
    strategyType?: number;
    type?: number;
    quoteDetails?: IQuoteDetails[];
    strategyModelVO?: ModelInfo;
}

export interface IQuoteEditParams {
    strategyId: number;
    strategyName: string;
    type?: number;
    newQuoteDetails?: IQuoteDetails[];
    newModelVO?: ModelInfo;
}

export interface IQuoteVOList {
    quoteId?: number;
    labelTypeName?: string;
    labelValueList?: string[];
    labelNameList?: string[];
    quote?: number;
    labelType?: TagCode;
    labelCode?: TagCode; // 新增字段，https://team.corp.kuaishou.com/task/T7205796，解决金融行业账户编辑时后端接口传给前端的 labelType 有误的问题
    coverNum?: number;
}

export enum QuoteStatus {
    'Enabled' = 1,
    'Disabled' = 0,
}
export interface IQuoteInfo {
    id?: number;
    accountId?: number; // 模糊匹配的id 和name
    strategyName?: string;
    strategyType?: number;
    type?: number;
    gmtCreate?: number;
    gmtModified?: number;
    ruleScope?: number;
    quoteVOList?: IQuoteVOList[];
    status?: QuoteStatus;
    strategyModelVO?: ModelInfo;
    operableEnable?: number;
}
export interface IContentInfo {
    quoteId: number;
    labelType: string;
    labelTypeName: string;
    labelValueList: string[];
    labelNameList: string[];
    quote: number;
}

export interface IDetail {
    gmtCreate: number;
    operationField: string;
    operationBefore: string;
    operationAfter: string;
    rowSpan: number;
}
export interface IEdit {
    strategyId?: number;
    strategyName?: string; // new name
    newQuoteDetails?: IQuoteDetails[];
}

export interface ITagItemInfo {
    label: string;
    value: string;
    children: ITagItemInfo[];
}

export interface ITagDetail {
    tagCode?: TagCode;
    tagName: string;
    tagItems: ITagInfo[];
}

// 地域标签的类型
export interface ITagInfo {
    label: string;
    value: string;
    children: ITagItemInfo[];
}

export interface ModelInfo {
    modelId?: number | string;
    modelName?: string;
    modelQuota?: string;
    predictionId?: number;
    predictionName?: string;
    predictionTime?: string;
    ruleStrength?: number;
    modelType?: number;
}

/**
 * @param {Object} params
 * @param {File} params.populationFile - 人群包文件
 * @param {number} params.type - 人群包类型
 * @param {number} params.orientationName - 人群包名
 */
export async function uploadPopulation(params) {
    const { data } = await axios.post<any, any>('/rest/dsp/tool/population/upload', params);
    return data;
}

/**
 * @param {Object} params
 * @param {string} params.sortingColumn - 排序的列名
 * @param {number} params.order - 1:升序; 2:降序
 * @param {number} params.status - 状态筛选
 * @param {number} params.orientationName - 人群包名
 * @param {Object} params.pageInfo
 */

export async function getPopulationList(params: {
    populationType: number;
    sortingColumn: string;
    status: number;
    orientationName: string;
    order: Order;
    pageInfo?: PageInfo;
}) {
    const { data, pageInfo } = await axios.post<
        any,
        RestfulRes<{ data: Population[]; pageInfo: PageInfo }>
    >('/rest/dsp/tool/population/list', params);
    return { data, pageInfo };
}

export async function getOwnPopulationList(params: any) {
    const { data } = await axios.post<any, RestfulRes<{ data: any[] }>>(
        '/rest/dsp/tool/population/list',
        params,
    );
    return data;
}

// 获取差异化出价可选择的人群
export async function getQuotePopulationList(params?: { keyword: string }) {
    const { data } = await axios.get<
        { keyword: string },
        { data: { first: Population[]; second: Population[] } }
    >('/rest/dsp/tool/population/list/quote', { params });
    return data;
}

// 获取差异化出价策略编辑时的内容
export async function getQuoteEditInfo(params: { strategyId: number }) {
    const { data } = await axios.get<{ strategyId: number }, { data: IQuoteInfo }>(
        '/rest/dmp/quote/strategy',
        { params },
    );
    return data;
}

// 获取地域标签列表
export async function getListCityLabel(params: { regionType: number }) {
    const { data } = await axios.get<
        {
            regionType: number;
        },
        {
            data: string;
        }
    >('/rest/dmp/quote/listCityLabels', { params });
    return data;
}

// 获取标签列表
export async function getQuoteLabelsList() {
    const { data } = await axios.get<any, { data: ITagDetail[] }>(
        '/rest/dmp/quote/listQuoteLabels',
    );
    return data;
}

// 创建策略 最多200
export async function quoteCreate(params: ICreatInfo) {
    return await axios.post<ICreatInfo, any>('/rest/dmp/quote/create', params);
}

// 编辑策略
export async function quoteEdit(params: IQuoteEditParams) {
    return await axios.post<IQuoteEditParams, any>('/rest/dmp/quote/edit', params);
}

// 策略列表 策略id检索或者名称模糊匹配
export async function getQuoteList() {
    const { data } = await axios.get<any, { data: IQuoteInfo[] }>('/rest/dmp/quote/list');
    return data;
}

// 编辑策略
export async function EditQuote(params: IEdit) {
    return await axios.put<IEdit, any>('/rest/dmp/quote/edit', params);
}

// 标签预估人数
export async function estimateAccounts(params: { tagCode: TagCode; tagValue: string }) {
    const { data } = await axios.get<
        {
            tagCode?: TagCode;
            tagValue: string;
        },
        {
            data: { coverNum: number };
        }
    >('/rest/dmp/quote/estimate', { params });
    return data;
}

// 策略开关
export async function quoteEnableStatus(params: {
    strategyId: number;
    enable: number; // 0:关 1:开
}) {
    return await axios.post<any, any>('/rest/dmp/quote/enable', params);
}

// 策略详情
export async function quoteDetail(params: { strategyId: number }) {
    const { data } = await axios.get<
        { strategyId: number },
        {
            data: IDetail[];
        }
    >('/rest/dmp/quote/detail', {
        params,
    });
    return data;
}

// 获得模型列表
export async function getListModel(modelType: number) {
    const { data } = await axios.get<
        { modelType: number },
        {
            data: ModelInfo[];
        }
    >('/rest/dmp/quote/listModels', {
        params: { modelType },
    });
    return data;
}

export async function deleteQuote(params: { strategyId: number }) {
    return await axios.get<
        { strategyId: number },
        {
            data: any;
        }
    >('/rest/dmp/quote//delete', { params });
}

export async function pushToAccounts(params: {
    orientationId: number;
    accounts: string;
    appId: number;
}) {
    const { fail } = await axios.post<any, { fail: number[] }>(
        '/rest/dsp/tool/population/pushToAccounts',
        params,
    );
    return fail;
}

export async function pushToBatchAccounts(params: {
    orientationId: number[];
    accounts: number[];
    appId: number;
}) {
    const { fail } = await axios.post<any, { fail: number[] }>(
        '/rest/dsp/tool/population/pushToAccounts/batch',
        params,
    );
    return fail;
}

export async function pushToAccountsCheck(params: { accounts: number[]; orientationId: number[] }) {
    const { available } = await axios.post<any, { available: number[] }>(
        '/rest/dsp/tool/population/pushToAccounts/check',
        params,
    );
    return available;
}

export function pushOrientationToAccountsCheck(data: {
    orientationIds: number[];
    accountIds: number[];
    appId: number;
}) {
    return axios
        .post<any, any>('/rest/dsp/tool/population/pushOrientationsToAccounts/check', data)
        .then((res) => res.data);
}

export async function validateAccount(params: { accountList: string[]; appId: number }) {
    const { errorAccount, selfAccount, invalidAccount, correctAccount } = await axios.post<
        any,
        any
    >('/rest/dmp/account/validate', params);
    return { errorAccount, selfAccount, invalidAccount, correctAccount };
}

export async function checkPopulationUnqiueName(orientationName: string) {
    const { isUniqueName } = await axios.post<any, { isUniqueName: boolean }>(
        '/rest/dsp/tool/population/check/uniqueName',
        { orientationName },
    );
    return isUniqueName;
}

/**
 * @param {Object} params
 * @param {number} params.startTime
 * @param {number} params.endTime
 */
export async function getPopulationUnit(params) {
    const { unitList } = await axios.post<any, { unitList: any[] }>(
        '/rest/dsp/control-panel/unit/search',
        params,
    );
    return unitList;
}

export async function getAccountIdList(params: { accountIds: number[] }) {
    const { list } = await axios.post<any, { list: { id: number; name: string }[] }>(
        '/rest/dsp/control-panel/unit/accountIdList',
        params,
    );
    return list;
}

export async function getAccountList(params?: { startTime: number; endTime: number }) {
    const res = await axios.post<
        any,
        {
            accountList: { id: number; name: string }[];
            clientName: string;
            accountIdList: number[];
            clientId: number;
            accountName: string;
            accountId: number;
        }
    >('/rest/dsp/control-panel/unit/allAccountList', params);

    return res;
}

export async function deleteBatchAccount(params: { orientationId: number[] }) {
    const { fail, success } = await axios.post<any, { fail: number[]; success: number[] }>(
        '/rest/dsp/tool/population/delete/batch',
        params,
    );
    return { fail, success };
}

export async function getpushWhiteList() {
    let { dmpOrientationPush } = await axios.post<any, any>('/rest/dsp/owner/info');
    return dmpOrientationPush;
}

export async function getUnitIdList(params: { unitIds: number[] }) {
    const { list } = await axios.post<any, { list: { name: string; id: number }[] }>(
        '/rest/dsp/control-panel/unit/unitIdList',
        params,
    );
    return list;
}

export async function getAllUnitList(params: { startTime: number; endTime: number }) {
    const { unitList } = await axios.post<any, { unitList: { name: string; id: number }[] }>(
        '/rest/dsp/control-panel/unit/allUnitList',
        params,
    );
    return unitList;
}

export async function getAllUnitListBrand(brandSourceType: number) {
    const { unitList } = await axios.post<any, { unitList: { name: string; id: number }[] }>(
        '/rest/brand/control-panel/unit/allUnitList',
        { brandSourceType },
    );
    await mockTimeout();
    return unitList;
}

export async function getAllOrderListBrand(brandSourceType: number) {
    const { orderList } = await axios.post<void, { orderList: { name: string; id: number }[] }>(
        '/rest/brand/control-panel/unit/allOrderList',
        { brandSourceType },
    );
    return orderList;
}

export async function searchUnitListBrand(params: {
    accountIds: number[];
    brandSourceType: number;
}) {
    const { unitList } = await axios.post<
        any,
        {
            unitList: { id: number; name: string }[];
        }
    >('/rest/brand/control-panel/unit/searchUnitList', params);
    return { unitList };
}

export async function getAccountListBrand() {
    const res = await axios.get<
        any,
        {
            accountList: { id: number; name: string }[];
            corporationId: number;
            corporationName: string;
            accountIdList: number[];
            accountName: string;
            accountId: number;
        }
    >('/rest/brand/control-panel/unit/allAccountList');
    await mockTimeout();
    return res;
}

// 创建人群包前的验证接口，参数不固定，先不写type
export async function checkCanProfile(param) {
    const { canProfile } = await axios.post<any, { canProfile: boolean }>(
        '/rest/dsp/tool/population/check/canProfile',
        param,
    );
    return canProfile;
}

/**
 * @param {Object} params
 * @param {string} params.orientationName
 * @param {number} params.ocpxActionType
 * @param {number} params.startTime
 * @param {number} params.endTime
 * @param {number[]} params.unitIdList
 */
export async function createPopulationByUnits(params) {
    const { data } = await axios.post<any, any>('/rest/dsp/tool/population/unit/transform', params);
    return data;
}

/**
 * @param {Object} params
 * @param {number} params.operateType - 运算关系类型
 * @param {string} params.orientationName - 定向人群名称
 * @param {Object} params.operateData - 人群运算数据
 * @param {number[]} params.operateData.source - 要运算的人群包ids
 * @param {number[]} params.operateData.exclude - 要排除的人群包ids
 */
export async function createPopulationByOperate(params) {
    const { data } = await axios.post<any, any>('/rest/dsp/tool/population/operate', params);
    return data;
}

export async function extendPopulation(params: {
    sourceId: number;
    isExcludeSeed: boolean;
    extendAmount: number;
    orientationName: string;
    targetPlatform: DeviceOsType;
}) {
    const { data } = await axios.post<any, any>('/rest/dsp/tool/population/extend', params);
    return data;
}

export async function createPaidPopulation(params: { sourceId: number; orientationName: string }) {
    const { data } = await axios.post<any, any>('/rest/dsp/tool/population/paid', params);
    return data;
}

export async function populationListPaid() {
    const { data } = await axios.get<any, { data: PaidPopulationItem[] }>(
        '/rest/dsp/tool/population/list/paid',
    );
    return data;
}

export async function OrderIndustryPaidPopulation(params: {
    firstIndustryId?: number;
    secondIndustryId?: number;
    orderType?: number; // 排序方式: 1-按消耗降序, 2-按上架时间升序, 3-按上架时间降序
    thirdPartyName?: string; // 数据来源
    name?: string; // 搜索人群包名称
}) {
    index++;
    const requestIndex = index;
    const { data } = await axios.post<any, any>(
        '/rest/dsp/tool/population/list/paid/industry',
        params,
    );
    if (requestIndex !== index) {
        throw '请求被覆盖';
    }
    return data;
}

export async function OrderCommonPaidPopulation(params: { orderType: number }) {
    const { data } = await axios.post<any, any>(
        '/rest/dsp/tool/population/list/paid/common',
        params,
    );
    return data;
}

export async function createReYunPaidPopulation(params: {
    coverNum: number;
    orientationName: string;
    activeLevelList: string[];
    artStyleList: string[];
    gameCategoryList: string[];
    gameThemeList: string[];
    payWillingList: string[];
}) {
    const { data } = await axios.post<any, any>(
        '/rest/dsp/tool/population/reyunPaidAudience',
        params,
    );
    return data;
}

export async function getChargedAudience() {
    const res = await axios.post<any, RestfulRes<{ paidAudience: PaidAudience[] }>>(
        '/rest/dsp/control-panel/unit/paid/audiences',
    );
    return res;
}

export function deletePopulation(orientationId: number) {
    return axios.post<any, RestfulRes>('/rest/dsp/tool/population/delete', { orientationId });
}

export function addThemeToPopulation(params: { sourceId: number; orientationName: string }) {
    return axios.post<any, RestfulRes>('/rest/dsp/tool/population/theme', params);
}

export async function getThemeList() {
    const { themeList } = await axios.post<
        any,
        RestfulRes<{
            themeList: ThemeAudience[];
            pageInfo: PageInfo;
        }>
    >('/rest/dmp/theme/list');
    return { themeList: themeList.map((i) => ({ ...i, name: i.label, audienceId: i.value })) };
}

export async function searchInetCelebrity(params: { query: string }) {
    const { apps } = await axios.get<any, { apps: InetCelebrity[] }>(
        '/rest/dsp/tool/labels/inet-celebrity/search',
        {
            params,
        },
    );

    return apps.map((item) => ({ ...item, name: item.secondLabel }));
}

/**
 * 商业兴趣数据获取
 */
export async function getBusinessInterests() {
    const { businessInterests } = await axios.post<
        any,
        RestfulRes<{ businessInterests: BusinessInterestsRep[] }>
    >('/rest/dsp/control-panel/unit/business/interests');
    const processedBusinessInterests = businessInterests.map((item, index) => ({
        value: index,
        label: item.firstIndustry,
        children: item.children.map((item) => ({
            value: item.id,
            label: item.secondIndustry,
            checkable: true,
        })),
    }));

    return processedBusinessInterests;
}

export function pushPopulation(id: number) {
    return axios.post('/rest/dsp/tool/population/pushOnline', { orientationId: id });
}

export function pushPopulationAgain(id: number) {
    return axios.post<any, RestfulRes<{}>>('/rest/dsp/tool/population/pushOnlineAgain', {
        orientationId: id,
    });
}

export async function getIndustryPopulationPrediction(params: { list: number[] }) {
    const { data } = await axios.post<any, { data: number }>(
        '/rest/dsp/tool/population/prediction/industryPreference',
        params,
    );
    return data;
}

export async function getPopulationPrediction(params: {
    typeName?: string;
    categories?: string[];
    appActionType?: number;
    level?: number;
    activeLevelList?: string[];
    artStyleList?: string[];
    gameCategoryList?: string[];
    gameThemeList?: string[];
    payWillingList?: string[];
    starId?: number[];
    starAudienceType?: number[];
}) {
    const { audiencePredictionNum } = await axios.post<any, any>(
        '/rest/dsp/tool/population/prediction',
        params,
    );
    return audiencePredictionNum;
}

export async function getPopulationPredictionV2(params: BaseReq) {
    const { audiencePredictionNum } = await axios.post<BaseReq, any>(
        '/rest/dsp/tool/population/prediction',
        params,
    );
    return audiencePredictionNum;
}

export async function createPopulationV2(params: CreatePopReq) {
    const data = await axios.post<CreatePopReq, any>(
        '/rest/dsp/tool/population/curing_create',
        params,
    );
    if (data) {
        addRadarEvent({
            name: 'SLI_DMP_NORMAL_POP_CREATE',
            result_type: 'success',
            message: '人群包创建成功',
        });
    } else {
        addRadarEvent({
            name: 'SLI_DMP_NORMAL_POP_CREATE',
            result_type: 'fail',
            message: '人群包创建失败',
        });
    }
    return data;
}

export async function getPopulationDetail(orientationId: number): Promise<PopulationDetail> {
    const { data } = await axios.post<
        never,
        {
            data: {
                id: number;
                orientationId: number;
                populationType: PopulationType;
                data: string;
                orientationName: '信息流播放公司主体-广告';
                brandSourceType: 2;
                brandHighPrecisionType: 2;
                brandSelectType: 0;
                status: number;
                createTime: number;
            };
        }
    >('/rest/dsp/tool/population/create_detail', {
        orientationId,
    });

    return {
        ...data,
        data: data && JSON.parse(data.data),
    };
}

export async function getProductKeywordRelation(params) {
    const { data = [] } = await axios.post<any, any>(
        '/rest/dsp/tool/population/productKeyword/relation',
        params,
    );
    return data;
}

export async function checkProductKeyword(params: { list: string[] }) {
    const { data } = await axios.post<any, any>(
        '/rest/dsp/tool/population/productKeyword/check',
        params,
    );
    return data;
}

export async function getPopulationDetailOverview(param) {
    const { data } = await axios.post<number, { data: PopulationDetailOverview }>(
        '/rest/dmp/populationDetail/overview',
        param,
    );
    return data;
}

/**
 * 获取人群包推送记录
 * @param param
 * @returns
 */
export async function getPopulationDetailPushRecord(param) {
    const { data } = await axios.post<number, { data: PopulationDetailPushRecord }>(
        '/rest/dmp/populationDetail/record',
        param,
    );
    return data;
}

export async function getPopulationDetailReport(param: ReportQuery) {
    const { data } = await axios.post<ReportQuery, { data: TableSourceItem[] }>(
        '/rest/dmp/populationDetail/report',
        param,
    );
    return data;
}

export function downloadPopulationDetail(params: ReportQuery) {
    window.open(`/rest/dmp/populationDetail/download?${qs.stringify(params)}`);
}

export async function getSimilarLabel(param: any) {
    const { apps } = await axios.post<any, { apps: string[] }>(
        '/rest/dsp/tool/labels/mobile/similar',
        param,
    );
    return apps;
}

export async function getPopulationMobile(params: { osType: 0 | 1 | 2 }) {
    const { data } = await axios.post<any, { data: CascaderOption }>(
        '/rest/dsp/tool/population/config',
        params,
    );
    const deviceIndexList = data.map((item) => ({
        ...item,
        children: item.children.map((item) => ({ ...item, checkable: true })),
    }));
    return deviceIndexList;
}

export async function getPopulationTypes() {
    const { list } = await axios.get<void, { list: string[] }>(
        '/rest/dsp/tool/population/type/list',
    );
    return list;
}

export async function getAppPermeabilityLabels() {
    const { list } = await axios.get<void, { list: ICascaderItem[] }>(
        '/rest/dsp/control-panel/appPermeability/labels/list',
    );
    const result = list.map((item) => ({
        label: item.label,
        value: item.label,
        children: item.children.map((item) => ({
            label: item.label,
            value: item.label,
            checkable: true,
        })),
    }));
    return result;
}

export async function checkAppNames(param: {
    appActionType: number;
    appDimension: number;
    appNameList: string;
}) {
    const { check } = await axios.post<
        any,
        { check: { msg: string; failedAppNames: string[]; successAppNames: string[] } }
    >('/rest/dsp/tool/labels/mobile/checkAppNames', param);
    return check;
}

export async function getBasicMessageRegion() {
    const { labels } = await axios.get<any, { labels: any }>(
        '/rest/dsp/tool/labels/basic_message/region',
    );
    return labels;
}
export async function getProfileTagIds(params: { tagType: number | string; attribute: any }) {
    const { data } = await axios.post<any, { data: number[] }>(
        '/rest/dmp/tags/getProfileTagIds',
        params,
    );
    return data;
}

// 检查人群包的绑定情况
export async function bindCheckForOrientation(params: { orientationIds: number[] }) {
    const { data } = await axios.post<any, { data: { bind: number[]; unbind: number[] } }>(
        '/rest/dsp/tool/population/bindCheckForOrientation',
        params,
    );
    await mockTimeout(1500);
    return data;
}

// 获取人群包绑定模版列表
export async function getBindDetailTemplate(params: {
    orientationId: number;
    populationType: number;
    pageNum: number;
    pageSize: number;
}) {
    const { data } = await axios.post<
        any,
        {
            result: 1;
            data: { templateBindDetail: AdDspTemplateDetail[]; dataEnd: boolean };
        }
    >('/rest/dsp/tool/population/templateBindDetail', params);
    return data;
}

// 获取人群包绑定unit列表
export async function getBindDetailUnit(params: {
    orientationId: number;
    populationType: number;
    pageNum: number;
    pageSize: number;
}) {
    const { data } = await axios.post<
        any,
        {
            result: 1;
            data: { unitBindDetail: AdDspUnitDetail[]; dataEnd: boolean };
        }
    >('/rest/dsp/tool/population/unitBindDetail', params);
    return data;
}

export interface AdDspTemplateDetail {
    templateId: number;
    templateName: string;
    status: number;
    unitCount: number; // 定向模版绑定的unit个数
    unitNames: string;
}
export interface AdDspUnitDetail {
    unitId: number;
    unitName: string;
    status: number;
}

// 解绑人群包与定向模版的绑定
export async function doUnbindTemplate(params: {
    orientationId: number;
    templateId: number;
    populationType: number; // 从population list中获取
}) {
    const { unbindTemp } = await axios.post<any, { result: number; unbindTemp: boolean }>(
        '/rest/dsp/tool/population/doUnbindTemplate',
        params,
    );
    return { unbindTemp };
}

// 解绑人群包与广告组的绑定
export async function doUnbindUnit(params: {
    orientationId: number;
    unitIds: number[];
    populationType: number;
}) {
    const { data } = await axios.post<
        any,
        {
            result: number;
            data: {
                success: number[];
                fail: number[];
            };
        }
    >('/rest/dsp/tool/population/doUnbindUnit', params);
    await mockTimeout();
    const unbindUnit = data.success.length > 0;
    return { unbindUnit, ...data };
}
export interface TagType {
    originTagName?: string;
    tagName: string;
    tagType: number;
    hover?: string;
    badge?: string;
    children: TagType[];
    tagId: number;
    frontTag: string;
    collected?: boolean;
    industryTag?: boolean;
    industryChildren?: object;
    version?: string;
}

export interface TagNameV2Item {
    firstTagName: string;
    secondTagName: string[];
}

export interface TagListV2Item {
    tagId: number;
    firstTagName: string;
    secondTagName: string;
    tagName: string;
    metaInfoId: number;
    tagVersion: number;
    version?: string;
    computeVersion: number;
    hover: string;
    desc: string;
    badge: string;
    tagType: number;
    appId: string;
    frontTag: string;
    collected: boolean;
    industryTag: boolean;
    newTag: boolean;
    aboutToExpire: boolean;
    hotLevel: number;
    usedTimes30Day: number;
}
/**
 *
 * @param pagePos TAG_AREA 标签计算   TAG_COMPUTE 标签专区
 */

export async function getTagList(pagePos: 1 | 0 | 2 = 0) {
    const { appId = 0 } = store.getState().common.userInfo;
    const baseUrl =
        process.env.REACT_APP_RUN_ENV === 'development'
            ? 'https://kapi.corp.kuaishou.com/mock/9752'
            : '';

    const { data } = await axios.post<any, { data: TagType[] }>(`/rest/dmp/tags/tagList`, {
        appId,
        pagePos,
    });

    if (data) {
        addRadarEvent({
            name: 'SLI_DMP_TAG_LIST_LOAD',
            result_type: 'success',
            message: '标签列表加载成功',
        });
    } else {
        addRadarEvent({
            name: 'SLI_DMP_TAG_LIST_LOAD',
            result_type: 'fail',
            message: '标签列表加载失败',
        });
    }

    if (pagePos === 0) {
        // 添加我的数据
        data.unshift({
            tagName: '我的人群',
            tagType: 0,
            // @ts-expect-error
            children: [{ tagName: '已有人群', tagType: 42, children: null, frontTag: 42 }],
        });
    }
    return data;
}

// 标签专区改版2.0  获取标签列表
export async function getTagListV2(pagePos: 1 | 0 | 2 = 0) {
    const { appId = 0 } = store.getState().common.userInfo;
    const baseUrl =
        process.env.REACT_APP_RUN_ENV === 'development'
            ? 'https://kapi.corp.kuaishou.com/mock/9752'
            : '';

    const { data } = await axios.post<any, { data: TagListV2Item[] }>(`/rest/dmp/tags/tagList/v2`, {
        appId,
        pagePos,
    });

    if (data) {
        addRadarEvent({
            name: 'SLI_DMP_TAG_LIST_LOAD',
            result_type: 'success',
            message: '标签列表加载成功',
        });
    } else {
        addRadarEvent({
            name: 'SLI_DMP_TAG_LIST_LOAD',
            result_type: 'fail',
            message: '标签列表加载失败',
        });
    }

    if (pagePos === 0) {
        // 添加我的数据
        data.unshift({
            tagName: '我的人群',
            tagType: 0,
            // @ts-expect-error
            children: [{ tagName: '已有人群', tagType: 42, children: null, frontTag: 42 }],
        });
    }
    return data;
}

export async function getTagDetail(tagId: number) {
    const { data } = await axios.post<
        any,
        {
            data: {
                tagId: number;
                coverNum: number;
                desc: string;
                potentialCustomers: string[];
                tagExamples: string;
                tagGroup: string;
                tagName: string;
                tagType: number;
                frontTag: string;
            };
        }
    >('/rest/dmp/tags/tagDetail', {
        tagId,
    });
    return data;
}

export async function searchTagService(params) {
    const { data } = await axios.get<{
        tagName: string;
        page: number;
        pageSize: number;
    }>('/rest/dmp/tags/tag_search', { params });
    return data;
}

export async function getCollectTagListService() {
    const { data } = await axios.get<any, { data: any }>('/rest/dmp/tags/tag_collection');
    return data;
}

export async function collectTagService(tagId: number, action: number) {
    const { data } = await axios.post<{ tagId: number; action: number }, { data: any }>(
        '/rest/dmp/tags/tag_collection',
        {
            tagId,
            action,
        },
    );
    return data;
}

export async function getRecommendTags(type: number) {
    const { data } = await axios.get<{ type: number[] }, { data: any }>(
        '/rest/dmp/tags/tag_recommend',
        {
            params: { type },
        },
    );
    return data;
}

// /rest/dmp/tags/tag_search
export async function searchTags(params: { tagName: string; page: number; pageNum: number }) {
    const { data } = await axios.get<any, { data: any }>('/rest/dmp/tags/tag_recommend', {
        params,
    });
    return data;
}

export async function getBannerList() {
    const { data } = await axios.post<
        any,
        { data: { bannerName: string; colorCode: string; pictureUrl: string; click: boolean }[] }
    >('/rest/dmp/tags/bannerList');
    return data;
}

export async function addCustomAudience(orientationId: number) {
    return await axios.post<any, any>('/rest/dmp/tags/addCustomAudience', {
        orientationId,
    });
}

export async function getBannerDetail(bannerName: string) {
    const { data } = await axios.post<
        any,
        {
            data: {
                coverNum: 1000;
                desc: 'ddd';
                name: 'ddd';
                orientationId: 1000;
                potentialCustomers: ['ddd', 'ddd'];
            }[];
        }
    >('/rest/dmp/tags/bannerDetail', {
        bannerName,
    });
    await mockTimeout(1500);
    return data;
}

export async function getIndustryColdAndRecommendTop3() {
    const { data: coldStart } = await axios.get<any, { data: IndustryPopulationDetail[] }>(
        '/rest/dmp/tags/industryNewTop3',
    );
    const { data: recommend } = await axios.get<any, { data: IndustryPopulationDetail[] }>(
        '/rest/dmp/tags/industryRecommendTop3',
    );
    return { coldStart, recommend };
}

export interface IndustryPopulationDetail {
    orientationId: 3004003;
    orientationName: string;
    recommendReason: string;
    desc: string;
    coverNum: number;
    rank: number;
    populationType: number;
    firstIndustryId: number;
    firstIndustryName: string;
    secondIndustryId: number;
    secondIndustryName: string;
    added: boolean;
    createDaysRank: number;
    qualityRank: number;
    costRank: number;
    dailyCoverNum: number;
}

export interface IndustryMore {
    coldStart: IndustryPopulationDetail[];
    myFirstIndustryId: number;
    mySecondIndustryId: number;
    recommend: IndustryPopulationDetail[];
}

export async function getIndustryColdAndRecommendMore(params: { sortType: number }) {
    const { data } = await axios.get<
        any,
        {
            data: IndustryMore;
        }
    >('/rest/dmp/tags/industryMore');
    return data;
}

export interface IndustryMoreNew {
    myFirstIndustryId: number;
    mySecondIndustryId: number;
    newMore: IndustryPopulationDetail[];
}

export async function getIndustryNewMore() {
    const { data } = await axios.post<
        any,
        {
            data: IndustryMoreNew;
        }
    >('/rest/dmp/tags/industryNewMore');
    return data;
}

export interface RecommendMore {
    myFirstIndustryId: number;
    mySecondIndustryId: number;
    recommendMore: IndustryPopulationDetail[];
}

export async function getIndustryRecommendMore(params: { sortType: number }) {
    const { data } = await axios.post<
        any,
        {
            data: RecommendMore;
        }
    >('/rest/dmp/tags/industryRecommendMore', params);
    return data;
}

// export async function getTagList(params) {
//     const { data } = await axios.post<any, { data: TagType[] }>('/rest/dmp/tags/tagList', params);
//     return data;
// }

export async function predictionByTags(params: {
    expression: string;
    description?: any;
    predictionType?: number;
}) {
    // 已有人群创建时，返回coverNum
    // const uuids = Object.keys(params.description);
    // if (uuids.length === 1 && params.description[uuids[0]].tagType === '42') {
    //     const attrs = params.description[uuids[0]].attribute;
    //     return attrs.coverNum;
    // }
    const { audiencePredictionNum } = await axios.post<any, { audiencePredictionNum: number }>(
        '/rest/dmp/population/predictionByTags',
        params,
    );
    return audiencePredictionNum;
}

export async function getGroupList() {
    const { data } = await axios.post<any, { data: string[] }>('/rest/dmp/population/groupList');
    return data;
}

// 人群分组列表 v2
export async function getGroupListV2() {
    const { data } = await axios.post<any, { data: IGroupInfo[] }>(
        '/rest/dmp/population/groupListV2',
    );
    return data;
}

// 编辑人群包名称
export async function editPopulationName(params: {
    orientationId: number;
    orientationName: string;
}) {
    const { data } = await axios.post<
        {
            orientationId: number;
            orientationName: string;
        },
        {
            data: string;
        }
    >('/rest/dmp/population/editName', params);
    return data;
}

// 编辑分组名称
export async function editGroupName(params: { oldGroupName: string; newGroupName: string }) {
    const { data } = await axios.post<
        {
            oldGroupName: string;
            newGroupName: string;
        },
        { data: string }
    >('/rest/dmp/population/groupNameEdit', params);
    return data;
}

// 校验分组名称是否唯一
export async function groupNameUnique(params: { groupName: string }) {
    const { data } = await axios.post<
        {
            groupName: string;
        },
        { data: boolean }
    >('/rest/dmp/population/groupNameUnique', params);
    return data;
}

// 删除分组
export async function deleteGroup(params: { groupName: string }) {
    const { data } = await axios.post<
        {
            groupName: string;
        },
        { data: string }
    >('/rest/dmp/population/groupDelete', params);
    return data;
}

// 批量移动人群包到分组
export async function movePopulationToGroup(params: {
    orientationIdList: number[];
    groupName: string;
}) {
    const { data } = await axios.post<
        {
            orientationIdList: number[];
            groupName: string;
        },
        {
            data: string;
        }
    >('/rest/dmp/population/moveToGroup', params);
    return data;
}

export async function createCustomAudience(params: any) {
    const {
        data: { orientationId },
    } = await axios.post<any, { data: { orientationId: number } }>(
        '/rest/dmp/population/createCustomAudience',
        params,
    );
    return orientationId;
}

export async function getECommerceList() {
    const { eCommerceList } = await axios.post<any, { eCommerceList: { children: any[] } }>(
        '/rest/dmp/theme/eCommerce/list',
    );
    return eCommerceList;
}

export async function addECommerceList(params: { orientationName: string; sourceId: number }) {
    await axios.post<any, any>('/rest/dsp/tool/population/eCommerce/add', params);
}

export async function getFansCategory() {
    const { labels } = await axios.post<void, any>('/rest/dsp/tool/labels/sec_follow_fever/labels');
    return labels;
}

export async function getExtendKeyword(params: { keyword: string }) {
    const {
        data: { expand, like },
    } = await axios.post<any, { data: { expand; like } }>(
        '/rest/dsp/tool/labels/behavior_message/extend_keyword',
        params,
    );

    return { expand, like };
}

export async function getSearchBehavior() {
    const { labels } = await axios.get<void, { labels: string[] }>(
        '/rest/dsp/tool/labels/behavior_message/search_behavior',
    );
    return labels;
}

export async function getExtendAuthor(author_id: number) {
    const { labels } = await axios.post<
        any,
        { labels: { label: string; value: any; children: any }[] }
    >('/rest/dsp/tool/labels/sec_follow_fever/extend_authors', { author_id });
    return labels;
}

export async function getKeywordAuthors(keyword: string) {
    const { labels } = await axios.post<
        any,
        { labels: { label: string; value: any; children: any }[] }
    >('/rest/dsp/tool/labels/sec_follow_fever/search_authors', {
        keyword,
    });
    return labels;
}

export async function getKeywordFollowFever(keyword: string) {
    const { labels, authors } = await axios.post<
        any,
        {
            labels: { label: string; value: any; children: any }[];
            authors: { label: string; value: any }[];
        }
    >('/rest/dsp/tool/labels/sec_follow_fever/search', {
        keyword,
    });
    return { labels, authors };
}

export async function getKeywordAllAuthor(keyword: string) {
    const { labels } = await axios.post<
        any,
        { labels: { label: string; value: any; children: any } }
    >('/rest/dsp/tool/labels/mmu_follow_fever/search_authors', {
        keyword,
    });
    return labels;
}

export async function getSecAuthor(keyword: string) {
    const { labels } = await axios.post<
        any,
        { labels: { label: string; value: any; children: any }[] }
    >(' /rest/dsp/tool/labels/sec_follow_fever/authors', {
        keyword,
    });
    return labels;
}

export async function getBehaviorSearchs(params) {
    const { keyword } = await axios.post<any, { keyword: Keyword[] }>(
        '/rest/dsp/tool/labels/keyword/query',
        params,
    );
    return Array.from(new Set(keyword));
}

export async function fetchBehavior() {
    const { behaviorInterest } = await axios.post<any, any>(
        '/rest/dsp/tool/labels/behaviorInterest',
    );
    return behaviorInterest;
}

export async function fetchBehavior1() {
    const { behaviorInterest } = await axios.post<any, any>(
        '/rest/dsp/tool/labels/behaviorInterest1',
    );
    return behaviorInterest;
}

export async function searchBehaviorInterest(k: string) {
    const { labels, keyword } = await axios.post<any, any>(
        '/rest/dsp/tool/labels/behaviorInterest/search',
        { keyword: k },
    );
    return { labels, keyword };
}

export async function batchCheck(p) {
    return await axios.post<any, any>('/rest/dsp/tool/labels/batch_check', p);
}

export async function getConsumeLevelCategories() {
    const { labels } = await axios.post<any, { labels: { label: string; value: number }[] }>(
        '/rest/dsp/tool/labels/consume_level/category',
    );
    return labels;
}
export interface SearchStarParams {
    starName: string;
}
export interface StarItem {
    id: number;
    name: string;
    userName: string | null;
    headUrl: string;
    isKs: number;
}

export async function getSearchStarList(params: SearchStarParams) {
    const starList = await axios.post<SearchStarParams, AxiosResponse<StarItem[]>>(
        '/rest/dsp/tool/population/star/query',
        params,
    );
    return starList.data;
}

export interface GoodsCategoryItem {
    // industry: {}[]
}

export interface GoodsAttributeItem {}
// 商品品类
export async function getGoodsCategory() {
    const goodsCategoryList = await axios.post<null, AxiosResponse<GoodsCategoryItem[]>>(
        '/rest/dsp/tool/labels/category_behavior',
    );
    return goodsCategoryList;
}

// 商品属性
export async function getGoodsAttribute() {
    const goodsAttributeList = await axios.post<null, AxiosResponse<GoodsAttributeItem[]>>(
        '/rest/dsp/tool/labels/category_behavior/attr',
    );
    return goodsAttributeList;
}

export async function searchGoodsCategory2ndAttribute(k: string) {
    const { inte, attr } = await axios.post<any, any>(
        '/rest/dsp/tool/labels/category_behavior/search',
        { searchWord: k },
    );
    return { inte, attr };
}

export async function searchGoodsCategory2ndAttributeNew(k: string) {
    if (isEmpty(k)) {
        return;
    }

    const genSearchResult = (dataList, type) => {
        return dataList.map((item) => {
            const uniqueName = item?.label;
            const uniqueId = item?.label;
            return {
                ...item,
                label: uniqueName,
                value: type === 'inte' ? 'cateList:' + uniqueId : 'cateAttrList:' + uniqueId,
                rawValue: type === 'inte' ? 'cateList:' + uniqueId : 'cateAttrList:' + uniqueId,
                rawLabel: uniqueId?.split('-')?.[uniqueId?.split('-')?.length - 1],
                name: uniqueName,
                hierarchyLabel: uniqueName,
                type,
                category: type === 'inte' ? 'cateList' : 'cateAttrList',
            };
        });
    };

    const { inte, attr } = await axios.post<any, any>(
        '/rest/dsp/tool/labels/category_behavior/search',
        { searchWord: k },
    );
    const genInte = genSearchResult(inte, 'inte');
    const genAttr = genSearchResult(attr, 'attr');
    return [...genInte, ...genAttr];
}

export async function uploadGoodsAttr(params) {
    const data = await axios.post<any, any>('/rest/dsp/tool/labels/upload/check', params);
    return {
        uploadCount: data?.simpleCount,
        itemCount: data?.checkResult?.length,
        successCount: data?.success?.length,
        failedCount: data?.failed?.length,
        duplicateCount: data?.duplicateCount,
        itemList: data?.checkResult?.map((v: { word: any; legal: any }) => ({
            id: v?.word,
            name: v?.word,
            type: Number(!v?.legal),
        })),
    };
}

export async function uploadKeywordsAttr(params) {
    const data = await axios.post<any, any>('/rest/dsp/tool/labels/upload/check', params);
    return {
        uploadCount: data?.simpleCount,
        itemCount: data?.checkResult?.length,
        successCount: data?.success?.length,
        failedCount: data?.failed?.length,
        duplicateCount: data?.duplicateCount,
        itemList: data?.checkResult?.map((v: { word: any; legal: any }) => ({
            id: v?.word,
            name: v?.word,
            type: Number(!v?.legal),
        })),
        success: data?.success,
    };
}

export async function downloadGoodsTemplate() {
    const data = await axios.post<null, null>('/rest/dsp/tool/labels/upload/check/template', null, {
        responseType: 'arraybuffer',
    });
    return data;
}

// 关键词模版下载
export async function downloadKeywordsTemplate() {
    const data = await axios.post<null, null>('/rest/dmp/keywords/v2/upload/template', null, {
        responseType: 'arraybuffer',
    });
    return data;
}

export function downloadFile(data: BlobPart, name: string) {
    if (!data) {
        message.error('下载失败，解析数据为空！');
        return;
    }
    // 创建一个新的url，此url指向新建的Blob对象
    let url = window.URL.createObjectURL(new Blob([data]));
    // 创建a标签，并隐藏改a标签
    let link = document.createElement('a');
    link.style.display = 'none';
    // a标签的href属性指定下载链接
    link.href = url;
    //setAttribute() 方法添加指定的属性，并为其赋指定的值。
    link.setAttribute('download', name + '.xls');
    document.body.appendChild(link);
    link.click();
}

/**
 * 文件下载
 * @param data
 * @param name
 * @returns
 */
export function downloadRawFile(data: BlobPart, name: string) {
    if (!data) {
        message.error('下载失败，解析数据为空！');
        return;
    }
    // 创建一个新的url，此url指向新建的Blob对象
    let url = window.URL.createObjectURL(new Blob([data]));
    // 创建a标签，并隐藏改a标签
    let link = document.createElement('a');
    link.style.display = 'none';
    // a标签的href属性指定下载链接
    link.href = url;
    // setAttribute() 方法添加指定的属性，并为其赋指定的值。
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
}

// 关键词标签下载模板
export function downloadKeywordFile(data: BlobPart, name: string) {
    if (!data) {
        message.error('下载失败，解析数据为空！');
        return;
    }
    // 创建一个新的url，此url指向新建的Blob对象
    let url = window.URL.createObjectURL(new Blob([data]));
    // 创建a标签，并隐藏改a标签
    let link = document.createElement('a');
    link.style.display = 'none';
    // a标签的href属性指定下载链接
    link.href = url;
    //setAttribute() 方法添加指定的属性，并为其赋指定的值。
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
}

// 通用模板下载接口
export async function downNormalTemplate(tagType: number) {
    const data = await axios.post<null, null>(
        '/rest/dsp/tool/labels/download/template',
        { tagType },
        {
            responseType: 'arraybuffer',
        },
    );
    return data;
}

// 通过搜索获取品牌list
export async function searchBrands(params: BrandSearchParams) {
    const { pageInfo, data } = await axios.get<BrandSearchParams, GetBrandResponse>(
        '/rest/dsp/tool/labels/brand_list',
        {
            params,
        },
    );
    return { pageInfo, data };
}

// 获取品牌详情列表
export async function getBrands(params: BrandsQuery) {
    const { pageInfo, data } = await axios.get<BrandsQuery, GetBrandResponse>(
        '/rest/dsp/tool/labels/brand_list',
        {
            params,
        },
    );
    return { pageInfo, data };
}

// 找相似品牌
export async function getSimilarBrands(brand: BrandItem) {
    const { data } = await axios.get<BrandsQuery, GetSimilarBrandsResponse>(
        '/rest/dsp/tool/labels/brand_extend',
        {
            params: {
                brandId: brand.brandId,
                firstLabelId: brand.firstLabelId,
                secondLabelId: brand.secondLabelId,
            },
        },
    );
    return data;
}

// 品牌名称搜索
// 请求URL /rest/dsp/tool/labels/brand_search
export async function searchBrandByName(params: BrandSearchParams) {
    return await axios.get<BrandSearchParams, SearchBrandByNameResponse>(
        '/rest/dsp/tool/labels/brand_search',
        {
            params,
        },
    );
}

// 上传品牌词包
// 请求URL:/rest/dsp/tool/labels/brand_existence 	"words": "玛莎拉蒂,凯迪拉克,凯迪拉克,苏泊尔（SUPOR）"
export async function checkBrandsExistence(words: string[]) {
    return client.post<any, any>('/rest/dsp/tool/labels/brand_existence', {
        words: words.join(','),
    });
}

// 上传品牌词包文件
// 请求URL:/rest/dsp/tool/labels/brand_upload
export async function uploadBrands(file) {
    const form = new FormData();
    form.append('file', file);
    return client.post<any, any>('/rest/dsp/tool/labels/brand_upload', form);
}

// 获取应用类别
export async function getAppCategoryList() {
    let { labels } = await axios.post<any, any>('/rest/dsp/tool/labels/mobile/appCategory');
    return labels;
}
// 获取应用名称
export async function getAppNameList() {
    const { labels } = await axios.post<any, any>('/rest/dsp/tool/labels/mobile/appName');

    const setKey = (list) => {
        return list.map((item) => {
            return {
                label: item.appCategoryName,
                value: item.appCategoryName,
                id: item.id,
                isChecked: false,
                appActionType: item.appActionType,
                cover: item.coverNum,
            };
        });
    };

    const result = setKey(labels);

    return result;
}
// 搜索应用列表
export async function searchAppCategory(params: { appActionType: number; searchWord: string }) {
    const { category, name } = await axios.post<any, any>(
        '/rest/dsp/tool/labels/mobile/appSearch',
        {
            ...params,
        },
    );

    const setValue = (list) => {
        return list.map((item) => {
            if (!item.value) {
                return {
                    ...item,
                    value: item.label,
                    cover: item.coverNum || 1,
                };
            } else {
                return item;
            }
        });
    };

    const categoryRes = setValue(category);
    const nameRes = setValue(name);

    return {
        category: categoryRes,
        name: nameRes,
    };
}

/**
 * 上传文件数据
 * @param formData
 * @returns
 */
export async function uploadFile(formData: any) {
    return await axios.post<any, any>('/rest/dsp/tool/population/brand_behavior/upload', formData);
}

/**
 * 获取添加品牌标签的聚合数据
 * @param params
 * @returns
 */
export async function getBrandBatchAggregation(params: any) {
    return await axios.post<any, any>(
        '/rest/dsp/tool/population/brand_behavior/aggregation',
        params,
    );
}

export async function getBrandCategories(params?: { cat?: string }) {
    const { labels } = await axios.get<any, { labels: any[] }>(
        '/rest/dsp/tool/labels/brand_category',
        {
            params,
        },
    );
    return labels;
}

// 获取游戏类别和主题数据
export async function getGameData(queryName = '') {
    const res = await axios.post<any, any>('/rest/dsp/tool/labels/game_type_theme', {
        queryName,
    });

    const { theme, type } = res.data;

    const modifyKeyValue = (item) => {
        return {
            label: item.name,
            value: item.id,
            id: item.id,
        };
    };

    const themeList = theme.map(modifyKeyValue);
    const typeList = type.map(modifyKeyValue);

    return {
        themeList,
        typeList,
    };
}

export async function getTagFormDetail(tagId: number) {
    const baseUrl =
        process.env.REACT_APP_RUN_ENV === 'development'
            ? 'https://kapi.corp.kuaishou.com/mock/9752'
            : '';

    let { data } = await client.post(`/rest/dmp/metaTool/getTagMeta`, {
        tagId: tagId,
    });

    if (isString(data)) {
        data = JSON.parse(data);
    }

    if (isEmpty(data.tagInfo?.layoutSchema)) {
        message.warning('标签数据未获取到');
        return;
    } else {
        if (isString(data.tagInfo?.layoutSchema)) {
            data.tagInfo['layoutSchema'] = JSON.parse(data.tagInfo?.layoutSchema);
        }
    }

    return data.tagInfo;
}

/**
 * 电商关键词获取
 * @param {number} typeId - The type of keyword you want to get.
 * @param {number} pid - parent id
 * @param {number} levelId - 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
 * @returns An array of keywords.
 */
export async function getKeywordsList(
    typeId: number,
    pid: number,
    levelId: number,
    ppid?: number | string,
    hierarchyLabel?: string,
) {
    if (!isNumber(typeId)) {
        return;
    }

    let { data } = await client.get(`/rest/dmp/ecomm/keyword/list/${typeId}/${pid}/${levelId}`);
    data = data.map((item) => {
        let temp = {
            loadData: true,
            label: item.label,
            value: item.value,
            cover: item.cover,
            name: item.label,
            level: item.level,
        };
        if (levelId === 1) {
            temp['cate1Id'] = item.value;
            temp['hierarchyLabel'] = item.label;
        }
        if (levelId === 2) {
            temp['cate1Id'] = pid;
            temp['cate2Id'] = item.value;
            temp['hierarchyLabel'] = `${hierarchyLabel}-${item.label}`;
        }
        if (levelId === 3) {
            temp['cate1Id'] = ppid;
            temp['cate2Id'] = pid;
            temp['keyword'] = item.value;
            temp['hierarchyLabel'] = `${hierarchyLabel}-${item.label}`;
        }
        return temp;
    });
    return data;
}

/**
 * 电商关键词搜索
 * @param {number} typeId - The type of the keyword
 * @param {string} keyword - The keyword to search for.
 * @returns An array of objects.
 */
export async function searcKeywordsList(typeId: number, keyword: string) {
    if (!isNumber(typeId)) {
        return;
    }
    if (isEmpty(keyword)) {
        return;
    }

    const genSearchResult = (dataList, type) => {
        return dataList.map((item) => {
            const uniqueName = `${item.cat1Name}-${item.cat2Name}-${item.keyword}`;
            const uniqueId = `${item.cat1Id}_${item.cat2Id}_${item.keyword}`;
            return {
                ...item,
                label: uniqueName,
                value: uniqueId,
                name: uniqueName,
                hierarchyLabel: uniqueName,
                type,
            };
        });
    };

    const { data } = await client.get(`/rest/dmp/ecomm/keyword/like/${typeId}/${keyword}`);
    const { absoluteList, likeList, recommendList } = data;
    const genAbsList = genSearchResult(absoluteList, 'absoluteList');
    const genLikeList = genSearchResult(likeList, 'likeList');
    const genRecommendList = genSearchResult(recommendList, 'recommendList');

    return [...genAbsList, ...genLikeList, ...genRecommendList];
}

// 用户权限及内外循环
export interface UserSpeedAuth {
    authorize: boolean;
    type: string;
    firstIndustryId: number;
    firstIndustryName: string;
    secondIndustryId: number;
    secondIndustryName: string;
    targetList: Array<string>;
    sceneList?: Array<string>;
}

export async function getUserSpeedAuthAndType(userId) {
    const baseUrl = 'https://kapi.corp.kuaishou.com/mock/13810';
    const params = {
        userId: userId,
    };
    const result = await axios.request<any>({
        url: `/sp/model/user/speed/auth`,
        method: 'get',
        params,
    });
    return result.data;
}

// 获取用户内外循环目标和场景
export async function getTargetAndScene() {
    const result = await axios.request<any>({
        url: `/sp/model/options`,
        method: 'get',
    });
    return result.data;
}

// 获取智能优选任务列表
export interface SampleListItem {
    // 样本名称
    orientationName: string;
    // 样本类型——正、负
    type: string;
}

export interface PopulationOptimizeListItem {
    // 创建时间
    createTime: number;
    // 任务id
    id: number;
    // 任务名称
    name: string;
    // 匹配人数
    predictionCrowdNum: number;
    // 样本列表
    sampleList: SampleListItem[];
    // 场景
    scene: string;
    // 状态
    status: string;
    // 目标
    target: string;
    // 最后更新时间
    updateTime: number;
}

export interface PopulationOptimizeList {
    code: string;
    count: number;
    data: PopulationOptimizeListItem[];
    dataPresent: boolean;
    extra: any;
    extraPresent: boolean;
    intCode: number;
    message: string;
    messageDisplay: string;
    success: boolean;
    traceId: string;
}

export async function getPopulationOptimizeList() {
    const baseUrl =
        process.env.REACT_APP_RUN_ENV === 'development'
            ? 'https://kapi.corp.kuaishou.com/mock/9752'
            : '';
    const result = await axios.request<PopulationOptimizeList>({
        url: `/sp/model/speed`,
        method: 'get',
    });
    return result;
}

// 获取任务详情
export async function getModelSpeedItemDetail(id) {
    const baseUrl =
        process.env.REACT_APP_RUN_ENV === 'development'
            ? 'https://kapi.corp.kuaishou.com/mock/13810'
            : '';

    const result = await axios.request<PopulationOptimizeListItem>({
        url: `/sp/model/speed/${id}`,
        method: 'get',
    });

    return result.data;
}

// 特征覆盖度和特征重要性
export interface IFeatureEstimateVo {
    categoryName: string;
    coveredRate: number;
    dataSetId: number;
    featureItemId: number;
    featureItemName: string;
    id: number;
    ivValue: number;
}

// 分页查询特征评估结果
export interface IPaginationResultFeatureEstimateVo {
    code: string;
    count: number;
    data: IFeatureEstimateVo[];
    dataPresent: boolean;
    extra: any;
    extraPresent: boolean;
    intCode: number;
    message: string;
    messageDisplay: string;
    pageNo: number;
    pageSize: number;
    success: boolean;
    validSampleNum: number;
    auc: number;
    ks: number;
    accuracy: number;
    precision: number;
    traceId: string;
    liftScore: number;
}

export async function getEstimateUsingGET(payload: {
    id: number;
    orderBy?: string;
    orderType?: string;
    pageNo?: number;
    pageSize?: number;
}) {
    const baseUrl =
        process.env.REACT_APP_RUN_ENV === 'development'
            ? 'https://kapi.corp.kuaishou.com/mock/9752'
            : '';

    const params = {
        orderBy: payload.orderBy,
        orderType: payload.orderType,
        // pageNo: payload.pageNo,
        pageSize: 100,
    };

    const result = await axios.request<IPaginationResultFeatureEstimateVo>({
        url: `/sp/model/speed/estimate/${payload.id}`,
        method: 'get',
        params,
    });

    return result.data;
}

// 获取模型训练结果
export async function getTrainResult(id) {
    const baseUrl =
        process.env.REACT_APP_RUN_ENV === 'development'
            ? 'https://kapi.corp.kuaishou.com/mock/13810'
            : '';

    const result = await axios.request<IPaginationResultFeatureEstimateVo>({
        url: `/sp/model/speed/result/${id}`,
        method: 'get',
    });

    return result.data;
}

/** Prediction Controller  人群分布 */
export interface IPredictionDistributionVo {
    segment: string;
    total: number;
}

export interface IListResultPredictionDistributionVo {
    code: string;
    count: number;
    data: IPredictionDistributionVo[];
    dataPresent: boolean;
    extra: any;
    extraPresent: boolean;
    intCode: number;
    message: string;
    messageDisplay: string;
    success: boolean;
    traceId: string;
}

export async function getPredictionDistributionUsingGET(payload: { id: number }) {
    const result = await axios.request<IListResultPredictionDistributionVo>({
        url: `/sp/model/speed/distribution/${payload.id}`,
        method: 'get',
    });

    return result.data;
}

// 人群圈选
export interface IResultCrowdSelect {
    maxScore: number;
    minScore: number;
    status: string;
    userNum: number;
}
export interface IListResultCrowdSelect {
    code: string;
    count: number;
    data: IResultCrowdSelect[];
    dataPresent: boolean;
    extra: any;
    extraPresent: boolean;
    intCode: number;
    message: string;
    messageDisplay: string;
    success: boolean;
    traceId: string;
}
export async function getCrowdSelect(id) {
    const result = await axios.request<IListResultCrowdSelect>({
        url: `/sp/model/speed/crowd/${id}`,
        method: 'get',
    });

    return result.data;
}

/** Model Controller  创建模型 */
export interface ISampleList {
    orientationId: number;
    orientationName: string;
    type: string;
}

export interface ICreateModelParam {
    /** 模型名称 */
    name: string;
    predictionCrowdNum: number; // 期望量级
    sampleList: ISampleList[]; // 正负样本
    scene?: string; // 场景
    target: string; // 目标
}

export interface ISimpleResultlong {
    code: string;
    data: number;
    dataPresent: boolean;
    extra: any;
    extraPresent: boolean;
    intCode: number;
    message: string;
    messageDisplay: string;
    success: boolean;
    traceId: string;
}

export async function createUsingPOST(payload: { param: ICreateModelParam }) {
    const data = {
        ...payload.param,
    };

    const result = await axios.request<ISimpleResultlong>({
        url: `/sp/model/speed/create`,
        method: 'post',
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return result.data;
}

/** Sample Controller  样本名称是否存在 */
export interface ISimpleResultboolean {
    code: string;
    data: boolean;
    dataPresent: boolean;
    extra: any;
    extraPresent: boolean;
    intCode: number;
    message: string;
    messageDisplay: string;
    success: boolean;
    traceId: string;
}

export async function checkNameUniqueUsingPOST(payload: { name?: string }) {
    const data = {
        name: payload.name,
    };

    const result = await axios.request<ISimpleResultboolean>({
        url: `/sp/model/speed/name/exists`,
        method: 'post',
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return result.data;
}

/** Model Controller  删除模型 */
export async function deleteUsingDELETE(payload: { id: any }) {
    const data = {
        taskIds: payload.id,
    };
    const result = await axios.request<ISimpleResultboolean>({
        url: `/sp/model/speed/delete`,
        method: 'post',
        data,
    });

    return result.data;
}

// 提取人群
export async function getExtractCrowd(id) {
    const result = await axios.request<ISimpleResultboolean>({
        url: `/sp/model/speed/extract/crowd/${id}`,
        method: 'get',
    });

    return result.data;
}

export async function getCancelCrowd(id) {
    const result = await axios.request<ISimpleResultboolean>({
        url: `/sp/model/speed/cancel/crowd/${id}`,
        method: 'get',
    });

    return result.data;
}

export async function getRetryCrowd(id) {
    const result = await axios.request<ISimpleResultboolean>({
        url: `/sp/model/speed/retry/crowd/${id}`,
        method: 'get',
    });

    return result.data;
}

// 创建人群失败时获取训练集详情
export async function getTrainDataset(id) {
    const result = await axios.request<ISimpleResultboolean>({
        url: `/sp/model/speed/training/dataset/${id}`,
        method: 'get',
    });

    return result.data;
}

// 获取分段覆盖人数
export async function getModelSpeedCrowdCover(payload: {
    maxScore: number;
    minScore: number;
    taskId: number;
}) {
    const data = {
        maxScore: payload.maxScore,
        minScore: payload.minScore,
        taskId: payload.taskId,
    };

    const result = await axios.request<ISimpleResultboolean>({
        url: `/sp/model/speed/cover/${payload.taskId}`,
        method: 'post',
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return result.data;
}

// 运算人群-创建人群包分组
export async function createGroup(groupName: string) {
    const { data } = await axios.post(
        '/rest/dmp/population/groupCreate',
        `groupName=${groupName}`,
        {
            headers: {
                'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
            },
        },
    );

    return data;
}

// 检查拆分或去重人群包数量是否超出账户限制
// 请求URL /rest/dsp/tool/population/checkAccountOverLimit
export async function checkAccountOverLimit(curSplitNum: number) {
    const res = await axios.get<any, { data: { overLimit: boolean; capacity: number } }>(
        `rest/dmp/population/checkAccountOverLimit/${curSplitNum}`,
    );
    return res.data;
}

// 点击预估
// 请求URL /rest/dmp/population/dedupePrediction
export async function reDeduplicate(params: {
    dedupeOrientations: { srcOid: number; orientationName: string }[];
}) {
    const data = await axios.post<any, RestfulRes<{ data: any[] }>>(
        `/rest/dmp/population/dedupePrediction`,
        params,
    );
    return data;
}

// 生成去重人群
// 请求URL  /rest/dmp/population/createDedupeOrientation
export async function generatePopulations(params: any) {
    const data = await axios.post<any, RestfulRes<{ data: any[] }>>(
        '/rest/dmp/population/createDedupeOrientation',
        params,
    );
    return data;
}

// 拆分人群正式计算
// 请求URL /rest/dsp/tool/population/splitOrientation
export async function formalSplitPopulations(params: any) {
    const data = await axios.post<any, RestfulRes<{ data: any[] }>>(
        '/rest/dsp/tool/population/splitOrientation',
        params,
    );
    return data;
}

//去重返回真实覆盖人数
// 请求URL /rest/dmp/population/batchPredictionByOrientationIdV2
export async function batchPredictionV2(params: { orientationIds: number[] }) {
    const res = await axios.post<any, RestfulRes<{ data: any }>>(
        '/rest/dmp/population/batchPredictionByOrientationIdV2',
        params,
    );
    return res.data;
}

export interface PushInfoResponse {
    text: string;
    ifSupportUpload: boolean;
}

export const getOrientationsToAccountsText = async (): Promise<PushInfoResponse> => {
    const res = await axios.get<PushInfoResponse>(
        '/rest/dsp/tool/population/pushOrientationsToAccounts/pushInfo',
    );
    return res.data;
};
